import { EventContactAttributes } from '@/types'
import { UpdateEventContactMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { contactTypes } from './contact_types'
import { Badge } from '../event_contact_type/badge'
import { useUserContact } from './useUserContact'
import { EditEventContactQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Button } from '@/components/base/button'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Inline as Error } from '@/components/error'

type Event = EditEventContactQuery['events']['nodes'][0]

interface Props {
  eventId: string
  id: string
  onSuccess: () => void
}

export const Form = ({ eventId, id, onSuccess }: Props) => {
  const {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading
  } = useForm<UpdateEventContactMutation, EventContactAttributes, EditEventContactQuery, Event>({
    queryVariables: { eventId, eventContactId: id },
    id,
    initializer: event => {
      const eventContact = event.eventContacts.nodes[0]
      return {
        eventId,
        userId: eventContact.user?.id || '',
        name: eventContact.name,
        email: eventContact.email,
        phone: eventContact.phone,
        contactType: eventContact.contactType,
      }
    },
    query: EditQuery,
    queryKey: 'events',
    mutation: UpdateMutation,
    resultKey: 'updateEventContact',
    refetchQueries: [TableQuery]
  })

  const [showDelete, setShowDelete] = useState(false)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  const { pickedUser } = useUserContact({ attributes, setAttributes })

  const onDelete = () => {
    setShowDelete(false)
    onSuccess()
  }

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { formBuilder.buildField({ name: 'Person', field: 'userId', type: 'user' }) }
        { formBuilder.buildField({ name: 'Name', field: 'name', type: 'text', disabled: pickedUser }) }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Email', field: 'email', type: 'email', disabled: pickedUser }) }
          { formBuilder.buildField({ name: 'Phone', field: 'phone', type: 'text', disabled: pickedUser }) }
        </div>
        { formBuilder.buildField({
          name: 'Contact type',
          field: 'contactType',
          type: 'select',
          options: Object.keys(contactTypes).map(ct => ({
            value: ct,
            display: <Badge eventContact={contactTypes[ct]} />
          })),
        })}
        <div className="flex flex-row justify-between">
          <Button color="red" onClick={() => setShowDelete(true) }>Delete</Button>
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
        </div>
        <DeleteDialog
          id={id}
          mutation={DeleteMutation}
          open={showDelete}
          refetchQueries={[TableQuery]}
          setOpen={setShowDelete}
          onSuccess={onDelete}
        />
      </div>
    </>
  )
}
